import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NewsletterSignup from "../components/NewsletterSignup";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { Calendar, User, ArrowLeft, ArrowRight, XLogo, LinkedinLogo, FacebookLogo, LinkSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

function readingTime(text = "") {
  const words = text.trim().split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/blog/${slug}`)
      .then((r) => setPost(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO(post ? {
    title: post.title,
    description: post.excerpt,
    image: post.cover_image,
    path: `/blog/${post.slug}`,
    type: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.excerpt,
      image: post.cover_image,
      author: { "@type": "Person", name: post.author_name },
      publisher: { "@type": "Organization", name: "CanHireSolution.com", logo: { "@type": "ImageObject", url: "https://canhiresolution.com/canhiresolution-logo.png" } },
      datePublished: post.published_at,
      dateModified: post.updated_at,
    },
  } : { title: "Loading…" });

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const tweetText = post ? `${post.title} via @CanHireSolution` : "";
  const tweetHashtags = (post?.tags || []).slice(0, 2).map((t) => t.replace(/[^a-zA-Z0-9]/g, "")).filter(Boolean).join(",");

  const openShare = (kind) => {
    const u = encodeURIComponent(shareUrl);
    const t = encodeURIComponent(tweetText);
    let href = "";
    if (kind === "twitter") {
      href = `https://twitter.com/intent/tweet?text=${t}&url=${u}` + (tweetHashtags ? `&hashtags=${encodeURIComponent(tweetHashtags)}` : "");
    } else if (kind === "linkedin") {
      href = `https://www.linkedin.com/sharing/share-offsite/?url=${u}`;
    } else if (kind === "facebook") {
      href = `https://www.facebook.com/sharer/sharer.php?u=${u}`;
    }
    if (href) window.open(href, "_blank", "noopener,noreferrer,width=600,height=600");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500">Loading…</div>;
  if (!post) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="pt-24 pb-16 max-w-2xl mx-auto px-4 text-center">
          <h1 className="font-display text-4xl mb-3">Post not found</h1>
          <Link to="/blog" className="text-brand font-semibold">← Back to blog</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <article className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/blog" className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-6 hover:text-slate-900" data-testid="back-to-blog">
          <ArrowLeft size={14} /> Back to blog
        </Link>
        {post.tags?.length > 0 && (
          <div className="text-[10px] uppercase tracking-wider font-semibold text-accent-red mb-3">{post.tags.join(" · ")}</div>
        )}
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-4">{post.title}</h1>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6">
          <span className="flex items-center gap-1"><User size={14} /> {post.author_name}</span>
          <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(post.published_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" })}</span>
          <span>{readingTime(post.content)} min read</span>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="share-toolbar">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mr-1">Share</span>
          <button
            onClick={() => openShare("twitter")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-colors text-xs font-semibold"
            data-testid="share-twitter-btn"
            aria-label="Share on X"
          >
            <XLogo size={14} weight="bold" /> Tweet
          </button>
          <button
            onClick={() => openShare("linkedin")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2] transition-colors text-xs font-semibold"
            data-testid="share-linkedin-btn"
            aria-label="Share on LinkedIn"
          >
            <LinkedinLogo size={14} weight="bold" /> LinkedIn
          </button>
          <button
            onClick={() => openShare("facebook")}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] transition-colors text-xs font-semibold"
            data-testid="share-facebook-btn"
            aria-label="Share on Facebook"
          >
            <FacebookLogo size={14} weight="bold" /> Facebook
          </button>
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors text-xs font-semibold"
            data-testid="share-copy-btn"
            aria-label="Copy link"
          >
            <LinkSimple size={14} weight="bold" /> Copy link
          </button>
        </div>
        {post.cover_image && (
          <img src={post.cover_image} alt={post.title} className="w-full mb-8" />
        )}
        <div className="prose prose-slate max-w-none prose-headings:font-display prose-headings:tracking-tight prose-a:text-brand prose-strong:text-slate-900">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
        </div>

        <div className="border-t border-slate-200 mt-10 pt-6">
          <div className="text-sm text-slate-600 mb-3">Found this helpful? Share it with someone who needs it.</div>
          <div className="flex flex-wrap items-center gap-2" data-testid="share-toolbar-footer">
            <button onClick={() => openShare("twitter")} className="inline-flex items-center gap-1.5 px-4 py-2 bg-slate-900 text-white hover:bg-black transition-colors text-xs font-semibold" data-testid="share-twitter-btn-footer">
              <XLogo size={14} weight="bold" /> Post on X
            </button>
            <button onClick={() => openShare("linkedin")} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#0A66C2] text-white hover:opacity-90 transition-opacity text-xs font-semibold" data-testid="share-linkedin-btn-footer">
              <LinkedinLogo size={14} weight="bold" /> Share on LinkedIn
            </button>
            <button onClick={() => openShare("facebook")} className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#1877F2] text-white hover:opacity-90 transition-opacity text-xs font-semibold" data-testid="share-facebook-btn-footer">
              <FacebookLogo size={14} weight="bold" /> Share on Facebook
            </button>
            <button onClick={copyLink} className="inline-flex items-center gap-1.5 px-4 py-2 border border-slate-300 text-slate-700 hover:bg-slate-100 transition-colors text-xs font-semibold" data-testid="share-copy-btn-footer">
              <LinkSimple size={14} weight="bold" /> Copy link
            </button>
          </div>
        </div>

        <div className="border-t border-slate-200 mt-12 pt-8">
          <NewsletterSignup source="blog-post" />
        </div>

        {post.related?.length > 0 && (
          <section className="border-t border-slate-200 mt-12 pt-8">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4">Read more</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {post.related.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="border border-slate-200 p-4 hover:-translate-y-1 hover:shadow-md transition-all" data-testid={`related-${p.slug}`}>
                  <div className="font-display text-base">{p.title}</div>
                  <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1 text-brand font-semibold">Read <ArrowRight size={12} weight="bold" /></div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
      <Footer />
    </div>
  );
}
